import { useEffect, useState } from "react";
import { Users, Building2, Plus, Trash2, KeyRound, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { tr } from "@/lib/i18n";
const TH = ({ children, className = "" }) => <th className={`px-4 py-3 text-left text-xs font-tech uppercase tracking-wider text-slate-500 ${className}`}>{children}</th>;

const Confirm = ({ item, title, text, onCancel, onConfirm }) => (
  <AlertDialog open={!!item} onOpenChange={(o) => !o && onCancel()}>
    <AlertDialogContent data-testid="admin-confirm-dialog">
      <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{text}</AlertDialogDescription></AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="admin-confirm-cancel">{tr("Annulla")}</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-amber-600 hover:bg-amber-700" data-testid="admin-confirm-ok">{tr("Elimina")}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const PasswordDialog = ({ user, onClose }) => {
  const [pw, setPw] = useState("");
  const save = async () => {
    try { await api.put(`/admin/users/${user.id}`, { password: pw }); toast.success(tr("Password impostata")); setPw(""); onClose(); }
    catch (ex) { toast.error(apiError(ex)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-pw-dialog">
        <DialogHeader><DialogTitle>{tr("Nuova password per")} {user?.name}</DialogTitle></DialogHeader>
        <Input type="password" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} placeholder={tr("Almeno 8 caratteri")} data-testid="admin-pw-input" />
        <DialogFooter><Button disabled={pw.length < 8} onClick={save} className="bg-[#0F52BA] hover:bg-[#0D47A1]" data-testid="admin-pw-save">{tr("Salva")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserRow = ({ u, me, groups, patch, onPw, onDel }) => (
  <tr className="border-b border-slate-100" data-testid={`admin-user-row-${u.id}`}>
    <td className="px-4 py-3"><div className="font-medium text-slate-900">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></td>
    <td className="px-4 py-3">
      <Select value={u.role} disabled={u.id === me} onValueChange={(v) => patch(u, { role: v })}>
        <SelectTrigger className="h-9 w-40" data-testid={`admin-user-role-${u.id}`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="user">{tr("Utente")}</SelectItem><SelectItem value="admin">{tr("Super-admin")}</SelectItem></SelectContent>
      </Select>
    </td>
    <td className="px-4 py-3">
      <Select value={u.group_id || "none"} onValueChange={(v) => patch(u, { group_id: v === "none" ? null : v })}>
        <SelectTrigger className="h-9 w-48" data-testid={`admin-user-group-${u.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{tr("Nessuna azienda")}</SelectItem>
          {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {u.group_id && (
        <Select value={u.group_role || "member"} onValueChange={(v) => patch(u, { group_role: v })}>
          <SelectTrigger className="h-8 w-48 mt-1.5 text-xs" data-testid={`admin-user-group-role-${u.id}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="member">{tr("Membro")}</SelectItem><SelectItem value="admin">{tr("Admin azienda")}</SelectItem></SelectContent>
        </Select>
      )}
    </td>
    <td className="px-4 py-3 font-tech">{u.projects}</td>
    <td className="px-4 py-3"><Switch checked={u.active} disabled={u.id === me} onCheckedChange={(v) => patch(u, { active: v })} data-testid={`admin-user-active-${u.id}`} /></td>
    <td className="px-4 py-3 text-right whitespace-nowrap">
      <Button variant="ghost" size="icon" title={tr("Imposta password")} onClick={() => onPw(u)} data-testid={`admin-user-pw-${u.id}`}><KeyRound className="w-4 h-4" /></Button>
      <Button variant="ghost" size="icon" title={tr("Elimina")} disabled={u.id === me} onClick={() => onDel(u)} className="hover:text-amber-700" data-testid={`admin-user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
    </td>
  </tr>
);

const GroupRow = ({ g, onRename, onDel }) => {
  const [edit, setEdit] = useState(null);
  return (
    <tr className="border-b border-slate-100" data-testid={`admin-group-row-${g.id}`}>
      <td className="px-4 py-3">
        {edit === null ? <span className="font-medium text-slate-900">{g.name}</span> : (
          <div className="flex gap-1.5">
            <Input className="h-9" value={edit} onChange={(e) => setEdit(e.target.value)} data-testid={`admin-group-name-input-${g.id}`} />
            <Button size="icon" variant="outline" onClick={async () => { if (await onRename(g, edit)) setEdit(null); }} data-testid={`admin-group-save-${g.id}`}><Check className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setEdit(null)}><X className="w-4 h-4" /></Button>
          </div>
        )}
      </td>
      <td className="px-4 py-3 font-tech" data-testid={`admin-group-members-${g.id}`}>{g.members}</td>
      <td className="px-4 py-3 font-tech">{g.projects}</td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <Button variant="ghost" size="icon" title={tr("Rinomina")} onClick={() => setEdit(g.name)} data-testid={`admin-group-edit-${g.id}`}><Pencil className="w-4 h-4" /></Button>
        <Button variant="ghost" size="icon" title={tr("Elimina")} onClick={() => onDel(g)} className="hover:text-amber-700" data-testid={`admin-group-delete-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
      </td>
    </tr>
  );
};

export default function Admin() {
  const { user, reload } = useAuth();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [gname, setGname] = useState("");
  const [pwUser, setPwUser] = useState(null);
  const [del, setDel] = useState(null);
  const load = () => Promise.all([api.get("/admin/users"), api.get("/admin/groups")]).then(([u, g]) => { setUsers(u.data); setGroups(g.data); });
  useEffect(() => { load(); }, []);

  const patch = async (u, body) => {
    try { await api.put(`/admin/users/${u.id}`, body); toast.success(tr("Utente aggiornato")); load(); if (u.id === user.id) reload(); }
    catch (ex) { toast.error(apiError(ex)); }
  };
  const addGroup = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/groups", { name: gname }); setGname(""); toast.success(tr("Azienda creata")); load(); } catch (ex) { toast.error(apiError(ex)); }
  };
  const rename = async (g, name) => {
    try { await api.put(`/admin/groups/${g.id}`, { name }); load(); reload(); return true; } catch (ex) { toast.error(apiError(ex)); return false; }
  };
  const confirmDel = async () => {
    try {
      await api.delete(del.kind === "user" ? `/admin/users/${del.item.id}` : `/admin/groups/${del.item.id}`);
      toast.success(del.kind === "user" ? tr("Utente eliminato") : tr("Azienda eliminata")); load(); reload();
    } catch (ex) { toast.error(apiError(ex)); }
    setDel(null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-6">
        <div className="sr-rise">
          <div className="text-xs font-tech uppercase tracking-wider text-[#0F52BA] mb-2">{tr("Amministrazione")}</div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight text-slate-900">{tr("Utenti e aziende")}</h1>
          <p className="text-slate-500 mt-2 max-w-2xl">{tr("I membri di un'azienda vedono e modificano tutti i progetti dell'azienda. Gli utenti senza azienda vedono solo i propri progetti. Gli admin azienda invitano i colleghi da \"La mia azienda\".")}</p>
        </div>
        <Tabs defaultValue="users" className="sr-rise" style={{ animationDelay: "80ms" }}>
          <TabsList><TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1.5" />{tr("Utenti (")}{users.length})</TabsTrigger>
            <TabsTrigger value="groups" data-testid="admin-tab-groups"><Building2 className="w-4 h-4 mr-1.5" />{tr("Aziende (")}{groups.length})</TabsTrigger></TabsList>
          <TabsContent value="users">
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead className="border-b border-slate-200"><tr><TH>{tr("Utente")}</TH><TH>{tr("Ruolo")}</TH><TH>{tr("Azienda")}</TH><TH>{tr("Progetti")}</TH><TH>{tr("Attivo")}</TH><TH /></tr></thead>
                <tbody>{users.map((u) => <UserRow key={u.id} u={u} me={user.id} groups={groups} patch={patch} onPw={setPwUser} onDel={(x) => setDel({ kind: "user", item: x })} />)}</tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="groups" className="space-y-4">
            <form onSubmit={addGroup} className="flex gap-2 max-w-md">
              <Input required maxLength={100} value={gname} onChange={(e) => setGname(e.target.value)} placeholder={tr("Nome dell'azienda (es. Sollar AG)")} data-testid="admin-group-new-input" />
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0D47A1]" data-testid="admin-group-create-btn"><Plus className="w-4 h-4 mr-1.5" />{tr("Crea")}</Button>
            </form>
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
              {groups.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm" data-testid="admin-groups-empty">{tr("Nessuna azienda.")}</div> : (
                <table className="w-full text-sm" data-testid="admin-groups-table">
                  <thead className="border-b border-slate-200"><tr><TH>{tr("Azienda")}</TH><TH>{tr("Membri")}</TH><TH>{tr("Progetti")}</TH><TH /></tr></thead>
                  <tbody>{groups.map((g) => <GroupRow key={g.id} g={g} onRename={rename} onDel={(x) => setDel({ kind: "group", item: x })} />)}</tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <PasswordDialog user={pwUser} onClose={() => setPwUser(null)} />
      <Confirm item={del} title={del?.kind === "user" ? tr("Eliminare l'utente?") : tr("Eliminare l'azienda?")}
        text={del?.kind === "user" ? tr("\"{0}\" verrà eliminato; i suoi progetti passano a te e le sue API key vengono revocate.", [del?.item.name]) : tr("I membri e i progetti di \"{0}\" restano, ma non saranno più condivisi.", [del?.item.name])}
        onCancel={() => setDel(null)} onConfirm={confirmDel} />
    </div>
  );
}
